import { useMemo, useCallback } from "react";
import { Tooltip, useMap } from "react-leaflet";
import useZoomLevel from "../utils/useZoomLevel";
import ColoredMarker from "./ColoredMarker";
import flyToLocation from "../utils/flyToLocation";

function MapMarkers({ locations, showCategoryMarker, activeTour, activeTourMarkers, currentTourLocation, handleMarkerSelection }) {

    const map = useMap();
    const zoom = useZoomLevel();  
    const clusterThreshold = 14     // Zoom level above which individual markers are shown

    // Variable to keep track of markers' visibility
    const isVisible = useCallback((loc) => {
        const categoryId = loc.category.id;
        const subcategoryId = loc.subcategory?.id;

        // Treat missing keys as "true" to match the Filter UI (showCategory[...] ?? true)
        const categoryChecked = showCategoryMarker?.[categoryId] ?? true;
        const subcatChecked = subcategoryId ? (showCategoryMarker?.[subcategoryId] ?? true) : false;
        const ownChecked = showCategoryMarker?.[`cat-${categoryId}-noSubcategory`] ?? true;

        // Locations with a subcategory should be visible if subcat or category is checked
        // Locations with no subcategory should be visible if ownChecked or category is checked
        const visibleByFilter = subcategoryId
            ? (subcatChecked || categoryChecked)
            : (ownChecked || categoryChecked);

        // Show the marker if it's visible according to the filter AND there is no active tour or 
        // it's part of the active tour AND the marker is not a tourLocation
        return (visibleByFilter && (!activeTour || activeTourMarkers.has(loc.id)) && !loc.tourLocation);
    }, [showCategoryMarker, activeTour, activeTourMarkers]);


    // Group visible markers by category for low-zoom clustering
    const groupedMarkers = useMemo(() => {
        const groups = {};

        // Iterate over all locations
        locations.forEach((loc) => {
            if (!isVisible(loc)) return;

            // Create groups by category
            const catId = loc.category.id;
            if (!groups[catId]) {
                groups[catId] = {
                    category: loc.category,
                    count: 0,
                    latSum: 0,
                    lngSum: 0,
                };
            }

            // Increment count and accumulate coordinates for averaging
            groups[catId].count++;
            groups[catId].latSum += loc.latitude || 0;
            groups[catId].lngSum += loc.longitude || 0;
        });

        return groups;
        // eslint-disable-next-line
    }, [locations, showCategoryMarker, activeTour, activeTourMarkers]);

    // Zoom in on the cluster when a cluster marker is clicked
    const handleClusterClick = (lat, long) => {
        if (!map) return;
        flyToLocation(map, lat, long, clusterThreshold + 1);
    };

    // If zoom is high enough, render individual markers
    if (zoom >= clusterThreshold) {
        return (
            <>
                {locations.filter(isVisible).map((loc) => (
                    <ColoredMarker
                        key={loc.id}
                        position={[loc.latitude, loc.longitude]}
                        color={loc.category?.color}
                        isActive={currentTourLocation?.id === loc.id}
                        subcatIcon={loc.subcategory?.icon || null}
                        onClick={() => handleMarkerSelection(loc)}
                        // onClick={() => handleMarkerClick(loc)}
                    >
                        <Tooltip direction="top" offset={[0, -10]} opacity={1}>
                            {loc.name}
                        </Tooltip>
                    </ColoredMarker>
                ))}
            </>
        );
    }

    // Cluster markers if zoom is too low
    return (
        <>
            {Object.values(groupedMarkers).map((g) => {
                if (!g.count) return null;

                // Calculate the cluster marker position from the average position
                // of all its markers
                const pos = [g.latSum / g.count, g.lngSum / g.count];

                return (
                    <ColoredMarker
                        key={`cat-${g.category.id}`}
                        position={pos}
                        color={g.category.color}
                        onClick={() => handleClusterClick(pos[0], pos[1])}
                    >
                        <Tooltip direction="top" offset={[0, -10]} opacity={1}>
                            {g.category.name} ({g.count})
                        </Tooltip>
                    </ColoredMarker>
                );
            })}
        </>
    );
}

export default MapMarkers;